import { SocialPlatform } from "@digital-twins/shared-types";
import { SocialPlatformIcon } from "../SocialPlatformIcon";
import { SOCIAL_PLATFORM_TEXT_CLASS, socialPlatformColorStyle } from "./socialChartColors";

export interface SocialChartTooltipRow {
  /** Stable row key and test id suffix: the platform, or a brand id. */
  key: string;
  label: string;
  /** A channel row shows its platform icon. */
  platform?: SocialPlatform;
  value: string;
  /** The row's series color, drawn as a swatch matching the chart and its legend. */
  color?: string;
}

interface SocialChartTooltipProps {
  title: string;
  rows: Array<SocialChartTooltipRow>;
  footer?: { label: string; value: string };
  /** Anchor x in px from the chart's left edge; the tooltip centres on it, kept inside `[0, containerWidth]`. */
  anchorX: number;
  containerWidth: number;
  testId: string;
}

const TOOLTIP_HALF_WIDTH = 80;

/** The one hover tooltip of a Topic chart: every series' value (channel or brand) for the hovered bucket. */
export function SocialChartTooltip(props: SocialChartTooltipProps) {
  const { title, rows, footer, anchorX, containerWidth, testId } = props;

  const left = Math.min(
    Math.max(anchorX, TOOLTIP_HALF_WIDTH),
    Math.max(TOOLTIP_HALF_WIDTH, containerWidth - TOOLTIP_HALF_WIDTH),
  );
  return (
    <div
      data-testid={testId}
      role="status"
      className="pointer-events-none absolute top-0 z-10 min-w-[9rem] -translate-x-1/2 rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md"
      style={{ left }}
    >
      <p className="mb-1 font-medium text-foreground">{title}</p>
      <ul className="space-y-0.5">
        {rows.map((row) => (
          <li
            key={row.key}
            data-testid={`social-chart-tooltip-row-${row.key}`}
            className="flex items-center justify-between gap-3"
          >
            <span className="flex items-center gap-1.5 text-muted-foreground">
              {row.color ? (
                <i
                  aria-hidden="true"
                  data-testid={`social-chart-tooltip-swatch-${row.key}`}
                  className="inline-block h-2.5 w-2.5 flex-none rounded-[3px]"
                  style={{ background: row.color }}
                />
              ) : null}
              {row.platform ? (
                <span
                  className={SOCIAL_PLATFORM_TEXT_CLASS}
                  style={socialPlatformColorStyle(row.platform)}
                >
                  <SocialPlatformIcon platform={row.platform} className="h-3 w-3" />
                </span>
              ) : null}
              <span className="truncate">{row.label}</span>
            </span>
            <span className="tabular-nums text-foreground">{row.value}</span>
          </li>
        ))}
      </ul>
      {footer ? (
        <p className="mt-1 flex items-center justify-between gap-3 border-t border-border pt-1">
          <span className="text-muted-foreground">{footer.label}</span>
          <span className="font-medium tabular-nums text-foreground">{footer.value}</span>
        </p>
      ) : null}
    </div>
  );
}
